import React, { useState } from 'react';
import { Grid, styled } from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';
import { Colors, PrimaryButton, Text } from 'library';
import { format12HourTime, formatShortWeekdayMonthDay } from 'shared/Dates';
import { IBusinessJobDetailsModel, ILeanShift } from '../../BusinessJob/models';
import { CalendarColors } from '../HiredShift';
import ProviderDisplay from './ProviderDisplay';
import CardInfo from './CardInfo';
import HourReportInfo from './HourReportInfo';
import ShiftCardMenu from './ShiftCardMenu';
import { isAfter, startOfTomorrow, isSameDay } from 'date-fns';
import RemoveProviderFromShfit from './RemoveProviderFromShfit';
import ActionRequired from './ActionRequired';
import { useHistory } from 'react-router';
import BanProviderAction from './BanProviderAction';
import CancelShiftModalTrigger from './CancelShiftModalTrigger';
import { IShift, ShiftJob, ShiftProviderUser } from 'models';
import MarkNoShowButton from './MarkNoShowButton';
import ClockInButton from './ClockInButton';

const Container = styled(Grid)({
    padding: 10,
});

export default function ShiftBaseCard({
    provider,
    pay,
    status,
    provider_id,
    ongoing_request,
    shift,
    refresh,
    provider_action_required,
    review_score,
}: {
    provider?: ShiftProviderUser;
    pay?: number | null;
    status: string;
    provider_id: number;
    shift: IShift | ILeanShift;
    ongoing_request: IBusinessJobDetailsModel | ShiftJob;
    provider_action_required?: boolean;
    refresh: () => void;
    review_score?: number;
}) {
    const history = useHistory();
    const hourReport = shift.hour_reports?.find((r) => r.provider.id === provider_id);
    const [banModal, setBanModal] = useState(false);

    const clockedIn = hourReport && hourReport.start && !hourReport.end;

    const clockedInStyle = clockedIn ? { border: 'solid', borderWidth: 2, borderColor: Colors.darkNavy } : {};

    const showRemoveProvider =
        ['UNFILLED', 'ON_STAFF'].includes(status) && isAfter(new Date(shift.start_date), startOfTomorrow());

    const completed = status === 'COMPLETED';

    const shouldRenderCancelShiftTrigger = (() => {
        const isOpenOrFilledFutureShift =
            ['UNFILLED', 'ON_STAFF'].includes(status) && isAfter(new Date(shift.start_date), startOfTomorrow());

        return (
            (isOpenOrFilledFutureShift || isSameDay(new Date(shift.start_date), new Date())) &&
            !shift.hour_reports.length
        );
    })();

    function renderManageShiftSection() {
        const showManageShiftActions =
            !hourReport &&
            isAfter(new Date(), new Date(shift.start_date)) &&
            isSameDay(new Date(shift.start_date), new Date());

        if (!showManageShiftActions) {
            return;
        }

        return (
            <Container container direction="row">
                <MarkNoShowButton shift={shift} provider={provider} onComplete={refresh} providerId={provider_id} />
                <ClockInButton shift={shift} provider={provider} onComplete={refresh} />
            </Container>
        );
    }

    function renderReviewAction() {
        if (!completed) {
            return;
        }

        return (
            <Grid item xs={12} style={{ padding: 5 }}>
                {review_score ? (
                    <Rating value={review_score} disabled={true} />
                ) : (
                    <PrimaryButton
                        buttonStyle={{ width: undefined }}
                        onClick={() => history.push(`/providers/review/${provider?.id}`)}
                    >
                        Review
                    </PrimaryButton>
                )}
            </Grid>
        );
    }

    return (
        <Grid item container direction="column" alignContent="center">
            <Grid
                item
                container
                style={{
                    borderRadius: 18,
                    backgroundColor: completed
                        ? Colors.lightGrey
                        : CalendarColors[ongoing_request.business_job_type ?? ''] ?? Colors.lightTurq,
                    padding: 10,
                    boxShadow: '0px 3px 6px 0px #00000029',
                    ...clockedInStyle,
                    marginBottom: 10,
                }}
            >
                <Grid
                    container
                    item
                    direction="row"
                    justify="space-between"
                    alignItems="flex-start"
                    style={{
                        borderBottom: 'solid',
                        borderBottomWidth: 1,
                        borderBottomColor: Colors.disabledGrey,
                        paddingRight: 10,
                        paddingLeft: 10,
                        paddingBottom: 10,
                    }}
                >
                    <Grid
                        xs={10}
                        container
                        item
                        direction="column"
                        justify="center"
                        alignContent="flex-start"
                        spacing={2}
                    >
                        {provider ? (
                            <>
                                <Grid item direction="row">
                                    <ProviderDisplay provider={provider} containerStyles={{ display: 'flex' }} />
                                </Grid>
                                {showRemoveProvider && provider ? (
                                    <Grid item direction="row">
                                        <RemoveProviderFromShfit
                                            shift={shift}
                                            provider={provider}
                                            provider_id={provider_id}
                                            setBanModal={setBanModal}
                                        />
                                        <BanProviderAction
                                            provider={provider}
                                            open={banModal}
                                            onClose={() => {
                                                setBanModal(false);
                                                refresh();
                                            }}
                                        />
                                    </Grid>
                                ) : null}

                                {shouldRenderCancelShiftTrigger && (
                                    <Grid item direction="row">
                                        <CancelShiftModalTrigger
                                            provider={provider}
                                            shift={shift}
                                            refresh={refresh}
                                            textStyle={{ textDecoration: 'none' }}
                                            buttonStyle={{ justifyContent: 'flex-start' }}
                                        />
                                    </Grid>
                                )}
                            </>
                        ) : (
                            <Text bold>{formatShortWeekdayMonthDay(shift.start_date)}</Text>
                        )}
                    </Grid>
                    <Grid
                        xs={2}
                        container
                        item
                        direction="row"
                        alignItems="center"
                        justify="flex-end"
                        style={{ paddingRight: 10 }}
                    >
                        <ShiftCardMenu
                            provider_id={provider_id}
                            provider={provider}
                            shift={shift}
                            ongoing_request={ongoing_request}
                            status={status}
                            refresh={refresh}
                        />
                    </Grid>
                    {renderReviewAction()}
                </Grid>
                <Grid container item style={{ paddingLeft: 8 }}>
                    <CardInfo
                        title="Schedule:"
                        content={`${format12HourTime(new Date(shift.start_date))} - ${format12HourTime(
                            new Date(shift.end_date),
                        )}`}
                    />
                    {pay ? <CardInfo title="Rate:" content={`$${pay}/hr`} /> : null}
                    <CardInfo title="Job Title:" content={ongoing_request.headline} />
                    {hourReport && provider ? <HourReportInfo hourReport={hourReport} /> : null}
                    {status === 'COMPLETED' ? (
                        <Text variant="caption" bold>
                            Completed
                        </Text>
                    ) : null}
                </Grid>
                {provider_action_required ? <ActionRequired /> : null}
                {renderManageShiftSection()}
            </Grid>
        </Grid>
    );
}
